'use client'

import {useMemo} from 'react'
import {Accordion, Button, SanityImage, Slideout, SlideshowNative} from '@local/ui/components'
import {useTranslate} from '@local/hooks'
import {cx} from '@local/utils/cx'

import {SanityImageType} from '@local/types'

import CloseSVG from '@local/assets/svgs/icon-close.svg'

export interface VariantPanelProps {
	isOpen: boolean
	onClickClose: () => void
	title: string,
	description: string,
	peoplePerRoom: number,
	gallery: Array<SanityImageType>
	uniqueItems: string
	notIncludedItems: string
	index: number
	price: number | undefined
	gids: Array<string>
	dates: Array<string>
}

export const VariantPanel = ({
	isOpen,
	onClickClose,
	index,
	title,
	gallery,
	uniqueItems,
	description,
	notIncludedItems,
}: VariantPanelProps) => {
	const locale = useTranslate()

	const _accordion = useMemo(() => {
		const arr: Array<{title:string, text:string}> = []
		if (uniqueItems) arr.push({title: locale('Unique Features'), text: uniqueItems})
		if (notIncludedItems) arr.push({title: locale('Not Included'), text: notIncludedItems})
		return arr
	}, [uniqueItems, notIncludedItems])

	return (
		<Slideout
			isOpen={isOpen}
			variant='right'
			onClickClose={onClickClose}
			className='bg-grey-4 text-white relative !px-0'>
			<div data-lenis-prevent className='relative overflow-y-scroll min-h-screen h-fit margin-px-1 flex flex-col pb-80 scrollbar-hide'>
				<div className='sticky top-0 z-2 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
					<h3 className='w-fit uppercase'><span>{locale('Room')}</span> <span>({index + 1})</span></h3>
					<Button
						small
						variant='icon'
						onClick={onClickClose}
						label='Close Menu'
						className='px-16 aspect-square'>
						<CloseSVG alt='Close Icon' />
					</Button>
				</div>

				{/* Gallery */}
				{!!gallery.length && (
					<SlideshowNative
						containerClassName='relative'
						withDotNavigation
						dotNavigationClassName={cx(
							'absolute bottom-20 left-1/2 -translate-x-1/2 justify-center gap-14',
							'[&>button]:max-w-[2.125rem] [&>button]:w-full [&>button]:bg-white',
							'lg:hidden',
						)}
						withPrevNextNavigation
						prevNextNavigationClassName='absolute top-1/2 inset-0 flex items-center justify-between z-1 lg-max:hidden'
						prevNextButtonVariant='secondary'
						prevNextButtonClassName={cx(
							'z-1 p-0 mx-10 w-38 h-38 [&_svg]:w-full [&_svg]:h-14 border-white',
							'disabled:border-grey-1',
						)}>
						{gallery.map((image, index) => (
							<SanityImage
								key={`gallery-${index}`}
								image={image}
								width={432}
								height={330}
								desktopWidth={432}
								mobileWidth={355}
								className='w-full aspect-[355/270] lg:aspect-[432/330] object-cover' />
						))}
					</SlideshowNative>
				)}

				{/* Info */}
				{title && <h4 className='text-11 tracking-wide uppercase leading-none mt-20 mb-12'>{title}</h4>}
				{description && <p className='text-12 leading-moderate text-grey-1 mb-20 text-[350]'>{description}</p>}

				{/* Accordion */}
				<Accordion
					textClassName='text-grey-1'
					items={_accordion} />
			</div>
		</Slideout>
	)
}
